import React, { useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { equals, clone, propEq, has } from "ramda";
import { isBlank, notBlank } from "../../../util/ext";

import { ContentFacetAutoCompleteListboxComponent } from "./ContentFacetAutocompleteListbox";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DoneIcon from "@material-ui/icons/Done";
import Grid from "@material-ui/core/Grid";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { matchSorter } from "match-sorter";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: `${theme.palette.primary.light} !important`,
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      color: "black",
      "& p": {
        color: "black",
      },
    },
  },

  search: {
    backgroundColor: "white",
  },

  listbox: {
    borderRadius: "5px",
    boxShadow: "0px 0px 30px rgba(149, 169, 177, 0.3)",
    border: `2px solid ${theme.palette.primary.main}`,
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },

  listIcon: {
    justifyContent: "flex-end",
  },

  listItemWithCode: {
    "& p": {
      fontSize: "1rem",
    },
  },
}));

const ContentFacetAutocomplete = props => {
  const { facet, handleFacetsSelected, selectedTerms } = props;
  const classes = useStyles();

  const facetClone = clone(facet);
  const handleChange = useCallback(
    (event, value) => {
      facetClone.terms = value;
      handleFacetsSelected(facetClone);
    },
    [facetClone, handleFacetsSelected],
  );

  useEffect(() => {
    //-- Divide date published terms into centuries/decades
    if (propEq("name", "contentDatePublished", facetClone)) {
      facetClone.contentDatePublished = facetClone;
    }
  }, [facetClone]);

  return (
    <MenuItem tabIndex={0} classes={{ root: classes.root }} disableRipple>
      <Autocomplete
        value={selectedTerms}
        autoComplete
        autoFocus
        className={classes.search}
        classes={{ listbox: classes.listbox }}
        id={`content-facet-autocomplete-${facet.name}`}
        fullWidth
        multiple
        disableCloseOnSelect
        getOptionLabel={option =>
          option.count ? `${option.label} (${option.count})` : option.label
        }
        getOptionSelected={(option, value) => {
          return isBlank(value) || option.label === value.label;
        }}
        limitTags={2}
        ListboxComponent={ContentFacetAutoCompleteListboxComponent}
        ListboxProps={{
          childSize: equals(facet.name, "bisacCode") ? 54 : null,
        }}
        options={facet.terms}
        onChange={handleChange}
        renderInput={params => (
          <TextField {...params} variant="outlined" placeholder="Search..." />
        )}
        filterOptions={(options, { inputValue }) => {
          return matchSorter(options, inputValue, {
            keys: ["label", "code", "queryParam"],
          });
        }}
        renderOption={(option, { selected }) => (
          <Grid container justify="space-between" alignItems="center">
            <Tooltip placement="bottom-start" title={option.label}>
              {facet.displayCode ? (
                <ListItemText
                  primaryTypographyProps={{ noWrap: true }}
                  className={classes.listItemWithCode}
                  primary={
                    notBlank(option.count)
                      ? `${option.label} (${option.count.toLocaleString()})`
                      : option.label
                  }
                  secondary={`${option.code}`}
                />
              ) : (
                <ListItemText
                  primaryTypographyProps={{ noWrap: true }}
                  primary={
                    has("count", option)
                      ? `${option.label} (${option.count.toLocaleString()})`
                      : option.label
                  }
                  secondary={
                    equals(facet.name, "bisacCode") ? option.code : null
                  }
                />
              )}
            </Tooltip>

            {selected && (
              <ListItemIcon className={classes.listIcon}>
                <DoneIcon color="primary" />
              </ListItemIcon>
            )}
          </Grid>
        )}
      />
    </MenuItem>
  );
};

export default ContentFacetAutocomplete;
